import { API, Storage } from 'aws-amplify';
import { downloadFromBlob } from 'utils/appUtils';
import { S3_BUCKET_SERVICE } from 'utils/const';

const uploadFilePromise = {};

export const getAllFilesFromS3 = async ({ s3Folder }) => {
  try {
    const result = await Storage.list(s3Folder);
    return { data: result };
  } catch (err) {
    return { error: err.message };
  }
};

export const getFileFromS3 = async ({ key }) => {
  try {
    const result = await Storage.get(key, {
      download: true,
      level: 'public',
      cacheControl: 'no-cache',
    });

    const reader = new FileReader();
    const readerPromise = new Promise((resolve, reject) => {
      reader.onloadend = (evt) => {
        if (evt.target.readyState === FileReader.DONE) {
          // DONE == 2
          const jsonData = JSON.parse(evt.target.result);

          resolve({ data: jsonData });
        }
      };

      reader.onerror = reject;
      reader.onabort = reject;
    });

    reader.readAsText(result.Body);

    return await readerPromise;
  } catch (err) {
    return { error: err.message };
  }
};

export const uploadFileToS3 = ({ s3Folder, file, moreOptions }) => {
  try {
    uploadFilePromise[file.name] = Storage.put(
      `${s3Folder}/${file.name}`,
      file,
      moreOptions,
    );
    return { data: uploadFilePromise[file.name] };
  } catch (err) {
    return { error: err.message };
  }
};

export const cancelFileUploadToS3 = async ({ fileName }) => {
  try {
    const result = await Storage.cancel(uploadFilePromise[fileName]);
    return { data: result };
  } catch (err) {
    return { error: err.message };
  }
};

export const downloadFileFromS3 = async ({ key, fileName }) => {
  try {
    const result = await Storage.get(key, {
      download: true,
    });
    downloadFromBlob(result.Body, fileName);
    return { data: result };
  } catch (err) {
    return { error: err.message };
  }
};

export const copyFileToS3Folder = async ({ srcKey, destinationKey }) => {
  try {
    const result = await Storage.copy({ key: srcKey }, { key: destinationKey });
    return { data: result };
  } catch (err) {
    return { error: err.message };
  }
};

export const removeFileFromS3 = async ({ key }) => {
  try {
    const result = await Storage.remove(key);
    return { data: result };
  } catch (err) {
    return { error: err.message };
  }
};

export const getLastUpdatedFileInFolder = async ({ s3Folder }) => {
  try {
    const result = await API.get(
      S3_BUCKET_SERVICE.API,
      S3_BUCKET_SERVICE.PATHS.GET_LAST_UPDATED_FILE_IN_FOLDER,
      {
        queryStringParameters: {
          s3Folder,
        },
      },
    );
    return { data: result };
  } catch (err) {
    return { error: err.message };
  }
};

export const getFileVersionHistory = async ({
  s3Folder,
  pageNumber,
  itemsPerPage,
}) => {
  try {
    const result = await API.get(
      S3_BUCKET_SERVICE.API,
      S3_BUCKET_SERVICE.PATHS.GET_FILE_VERSION_HISTORY,
      {
        queryStringParameters: {
          s3Folder,
          pageNumber,
          itemsPerPage,
        },
      },
    );
    return { data: result };
  } catch (err) {
    return { error: err.message };
  }
};

export const listFilesInS3Folder = async ({ s3Folder }) => {
  try {
    const result = await API.get(
      S3_BUCKET_SERVICE.API,
      S3_BUCKET_SERVICE.PATHS.LIST_FILES_IN_S3_FOLDER,
      {
        queryStringParameters: {
          s3Folder,
        },
      },
    );
    return { data: result };
  } catch (err) {
    return { error: err.message };
  }
};

export const deleteAllVersionsOfObject = async ({ key }) => {
  try {
    const result = await API.get(
      S3_BUCKET_SERVICE.API,
      S3_BUCKET_SERVICE.PATHS.DELETE_ALL_VERSIONS_OF_OBJECT,
      {
        queryStringParameters: {
          objectKey: key,
        },
      },
    );
    return { data: result };
  } catch (err) {
    return { error: err.message };
  }
};

export const deleteLatestVersionOfObject = async ({ key }) => {
  try {
    const result = await API.get(
      S3_BUCKET_SERVICE.API,
      S3_BUCKET_SERVICE.PATHS.DELETE_LATEST_VERSION_OF_OBJECT,
      {
        queryStringParameters: {
          objectKey: key,
        },
      },
    );
    return { data: result };
  } catch (err) {
    return { error: err.message };
  }
};

export const downloadObjectVersion = async ({
  key,
  versionId,
  isMiPulseBucket,
}) => {
  try {
    const result = await API.get(
      S3_BUCKET_SERVICE.API,
      S3_BUCKET_SERVICE.PATHS.DOWNLOAD_OBJECT_VERSION,
      {
        queryStringParameters: {
          objectKey: key,
          versionId,
          isMiPulseBucket,
        },
      },
    );
    return { data: result };
  } catch (err) {
    return { error: err.message };
  }
};

export const publishToMipulse = async ({
  srcKey: sourceObjectKey,
  destinationKey: destinationObjectKey,
}) => {
  try {
    const result = await API.get(
      S3_BUCKET_SERVICE.API,
      S3_BUCKET_SERVICE.PATHS.PUBLISH_TO_MIPULSE,
      {
        queryStringParameters: {
          sourceObjectKey,
          destinationObjectKey,
          publishedFrom: process.env.REACT_APP_BUILD_ENV,
        },
      },
    );
    return { data: result };
  } catch (err) {
    return { error: err.message };
  }
};

export const publishFilesToMipulseUAT = async ({
  srcKey: sourceObjectKey,
  destinationKey: destinationObjectKey,
}) => {
  try {
    const result = await API.get(
      S3_BUCKET_SERVICE.API,
      S3_BUCKET_SERVICE.PATHS.PUBLISH_FILES_TO_MIPULSE_UAT,
      {
        queryStringParameters: {
          sourceObjectKey,
          destinationObjectKey,
          publishedFrom: process.env.REACT_APP_BUILD_ENV,
        },
      },
    );
    return { data: result };
  } catch (err) {
    return { error: err.message };
  }
};

export const uploadJsonToS3 = async ({ json, s3Folder, fileName }) => {
  try {
    const result = await Storage.put(
      `${s3Folder}/${fileName}`,
      JSON.stringify(json),
      {
        contentType: 'application/json',
      },
    );
    return { data: result };
  } catch (err) {
    return { error: err.message };
  }
};

export const readCsvInS3 = async ({ key }) => {
  try {
    const result = await API.get(
      S3_BUCKET_SERVICE.API,
      S3_BUCKET_SERVICE.PATHS.READ_CSV_DATA,
      {
        queryStringParameters: {
          key,
        },
      },
    );
    return { data: result };
  } catch (err) {
    return { error: err.message };
  }
};
